import React from 'react'

import { Slider } from 'components/ui/slider'
import { UnderlinedTooltip } from 'components/ui/UnderlinedTooltip'

import { emitEmployerOnboardingEvent } from '../../utils'
import { useSelfServeExplorerContext } from '../QuoteSelfServeExplorerContext'

interface AdvancedOptionsProps {
  setDependentCoverage: React.Dispatch<React.SetStateAction<number>>
}

export function AdvancedOptions ({ setDependentCoverage }: AdvancedOptionsProps) {
  const { filterValues, setFilterValues } = useSelfServeExplorerContext()
  return (
    <div className='grid md:grid-cols-[auto_1fr] gap-6'>
      <div className='grid grid-cols-subgrid md:col-span-2 gap-4'>
        <h3 className='heading-sm col-span-2'>
          <UnderlinedTooltip content='Configure how much you want to contribute to your employees&apos; health insurance costs' className='w-fit'>
            Contribution
          </UnderlinedTooltip>
        </h3>
        <div className='grid grid-cols-subgrid md:col-span-2 gap-1 md:gap-4'>
          <span className='body'>Your contribution</span>
          <div className='flex items-center gap-4'>
            <span className='text-xs text-gray-500 min-w-4'>
              <span className='tabular-nums'>{Math.round(filterValues.employerEmployeeContribution * 100)}%</span>
            </span>
            <div className='w-full'>
              <Slider
                min={0}
                max={100}
                value={[filterValues.employerEmployeeContribution * 100]}
                trackColor='bg-gray-300'
                rangeColor='bg-black'
                onValueChange={value => {
                  const newValue = value[0] / 100
                  if (filterValues.employerEmployeeContribution !== newValue) {
                    setFilterValues({ ...filterValues, employerEmployeeContribution: newValue })
                    emitEmployerOnboardingEvent('[ER] AllowanceTool custom cost sharing updated', {})
                  }
                }}
                data-testid='employer-employee-contribution-slider'
              />
            </div>
          </div>
        </div>
      </div>
      <hr className='md:col-span-2' />
      <div className='grid grid-cols-subgrid md:col-span-2 gap-4'>
        <h3 className='heading-sm col-span-2'>
          <UnderlinedTooltip content='The percentage of health insurance cost you&apos;ll cover for your employees&apos; dependents' className='w-fit'>
            Dependent coverage
          </UnderlinedTooltip>
        </h3>
        <div className='grid grid-cols-subgrid md:col-span-2 gap-1 md:gap-4'>
          <span className='body'>First dependent</span>
          <div className='flex items-center gap-4'>
            <span className='text-xs text-gray-500 min-w-4'>
              <span className='tabular-nums'>{Math.round(filterValues.employerSpouseContribution * 100)}%</span>
            </span>
            <div className='w-full'>
              <Slider
                trackColor='bg-gray-300'
                rangeColor='bg-black'
                min={0}
                max={100}
                value={[Math.round(filterValues.employerSpouseContribution * 100)]}
                onValueChange={value => {
                  const newValue = value[0] / 100
                  if (filterValues.employerSpouseContribution !== newValue) {
                    setFilterValues({ ...filterValues, employerSpouseContribution: newValue })
                    emitEmployerOnboardingEvent('[ER] AllowanceTool custom dependent 1 coverage updated', {})
                  }
                  setDependentCoverage(value[0] / 100)
                }}
                data-testid='employer-spouse-contribution-slider'
              />
            </div>
          </div>
        </div>
        <div className='grid grid-cols-subgrid md:col-span-2 gap-1 md:gap-4'>
          <span className='body'>Additional dependent(s)</span>
          <div className='flex items-center gap-4'>
            <span className='text-xs text-gray-500 min-w-4'>
              <span className='tabular-nums'>{Math.round(filterValues.employerChildContribution * 100)}%</span>
            </span>
            <div className='w-full'>
              <Slider
                trackColor='bg-gray-300'
                rangeColor='bg-black'
                min={0}
                max={100}
                value={[Math.round(filterValues.employerChildContribution * 100)]}
                onValueChange={value => {
                  const newValue = value[0] / 100
                  if (filterValues.employerChildContribution !== newValue) {
                    setFilterValues({ ...filterValues, employerChildContribution: newValue })
                    emitEmployerOnboardingEvent('[ER] AllowanceTool custom dependent addtl coverage updated', {})
                  }
                }}
                data-testid='employer-child-contribution-slider'
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
